import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react"

type Theme = "light" | "dark"

const STORAGE_KEY = "nanoplay-theme"

interface ThemeContextType {
    theme: Theme
    toggleTheme: () => void
    setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

/**
 * Resolve o tema inicial: preferência salva > preferência do SO > dark.
 * Executado tanto aqui quanto no snippet inline do index.tsx (anti-flash);
 * mantê-los alinhados garante que o estado do React case com o DOM já pintado.
 */
export function resolveInitialTheme(): Theme {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored === "light" || stored === "dark") return stored
    } catch {
        /* localStorage indisponível: segue para o fallback */
    }
    if (
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: light)").matches
    ) {
        return "light"
    }
    return "dark"
}

function applyTheme(theme: Theme) {
    const root = document.documentElement
    root.classList.toggle("dark", theme === "dark")
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [theme, setThemeState] = useState<Theme>(() =>
        document.documentElement.classList.contains("dark") ? "dark" : "light"
    )

    const setTheme = useCallback((next: Theme) => {
        setThemeState(next)
        applyTheme(next)
        try {
            localStorage.setItem(STORAGE_KEY, next)
        } catch {
            /* persistência best-effort */
        }
    }, [])

    const toggleTheme = useCallback(() => {
        setTheme(theme === "dark" ? "light" : "dark")
    }, [theme, setTheme])

    // Garante que o DOM reflita o estado inicial do React na montagem.
    useEffect(() => {
        applyTheme(theme)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    )
}

export function useTheme(): ThemeContextType {
    const ctx = useContext(ThemeContext)
    if (!ctx) {
        throw new Error("useTheme deve ser usado dentro de <ThemeProvider>")
    }
    return ctx
}
